import * as d3 from 'd3';
import type { I18n } from '@learnforge/i18n';

export interface TreeNode<K extends string = string> {
  labelKey: K;
  children?: Array<{ probability: number; node: TreeNode<K> }>;
}

export interface TreeHandle<K extends string = string> {
  element: SVGSVGElement;
  update(root: TreeNode<K>): void;
  destroy(): void;
}

/**
 * Adjust child at `index` to `newProbability`, rescaling siblings so the row
 * sums to 1. If all siblings (including the edited one) are zero, equal-split.
 */
export function rebalance(
  children: Array<{ probability: number; node: TreeNode }>,
  index: number,
  newProbability: number,
): void {
  const clamped = Math.max(0, Math.min(1, newProbability));
  children[index]!.probability = clamped;
  const others = children.filter((_, i) => i !== index);
  const remaining = 1 - clamped;
  const sumOthers = others.reduce((s, c) => s + c.probability, 0);
  if (sumOthers > 0) {
    const scale = remaining / sumOthers;
    for (let i = 0; i < children.length; i++) {
      if (i === index) continue;
      children[i]!.probability *= scale;
    }
  } else {
    const share = others.length > 0 ? remaining / others.length : 0;
    for (let i = 0; i < children.length; i++) {
      if (i === index) continue;
      children[i]!.probability = share;
    }
  }
}

export function leafProbabilities(root: TreeNode): number[] {
  const out: number[] = [];
  function walk(node: TreeNode, acc: number) {
    if (!node.children || node.children.length === 0) {
      out.push(acc);
      return;
    }
    for (const child of node.children) walk(child.node, acc * child.probability);
  }
  walk(root, 1);
  return out;
}

/**
 * The `labelKey` path from root to each leaf (root included), in the same
 * order as {@link leafProbabilities}.
 */
export function leafPaths<K extends string = string>(root: TreeNode<K>): K[][] {
  const out: K[][] = [];
  function walk(node: TreeNode<K>, acc: K[]) {
    const path = [...acc, node.labelKey];
    if (!node.children || node.children.length === 0) {
      out.push(path);
      return;
    }
    for (const child of node.children) walk(child.node, path);
  }
  walk(root, []);
  return out;
}

/**
 * The edge probabilities from root to each leaf (root excluded — it has no
 * incoming edge), in the same order as {@link leafProbabilities} /
 * {@link leafPaths}.
 */
export function leafBranchProbabilities(root: TreeNode): number[][] {
  const out: number[][] = [];
  function walk(node: TreeNode, acc: number[]) {
    if (!node.children || node.children.length === 0) {
      out.push(acc);
      return;
    }
    for (const child of node.children) walk(child.node, [...acc, child.probability]);
  }
  walk(root, []);
  return out;
}

export interface CreateTreeOptions<K extends string = string> {
  container: HTMLElement;
  i18n: I18n<K>;
  initial: TreeNode<K>;
  onChange?(root: TreeNode<K>): void;
  /** Click-to-edit + hover affordance on probability labels. Default false. */
  editable?: boolean;
  /**
   * Scope of a committed probability edit. `'node'` (default) rebalances
   * only the edited node's own children — today's behavior. `'tree'`
   * rebalances the same index in EVERY internal node of the whole tree that
   * has more than that many children (walked from root) — used by the
   * repeated-event custom preset, where every occurrence shares one
   * probability vector and editing index `i` means "P(event_i) changed",
   * full stop.
   */
  editScope?: 'node' | 'tree';
}

/**
 * Leaf-column layout constants (px). Leaves sit `COL` apart in traversal
 * order; an internal node sits above the midpoint of its first and last
 * child so the tree stays content-sized instead of halving its spread every
 * level (the old `hSpan / 2` scheme crowded deep leaves together).
 */
const COL = 64;
const VSTEP = 80;
const SIDE = 40;
const TOP = 30;
const BOTTOM = 24;

interface Pos {
  x: number;
  y: number;
}

interface Layout<K extends string> {
  positions: Map<TreeNode<K>, Pos>;
  leafCount: number;
  maxDepthEdges: number;
}

/**
 * Leaf `i` (enumerated in traversal order) gets `x = SIDE + i * COL`; an
 * internal node's `x` is the midpoint of its first and last child's `x` (not
 * an average over all children). `y = TOP + depth * VSTEP`. `depth` counts
 * edges from the root (root itself is depth 0).
 */
function computeLayout<K extends string>(root: TreeNode<K>): Layout<K> {
  const positions = new Map<TreeNode<K>, Pos>();
  let leafCount = 0;
  let maxDepthEdges = 0;

  function computeX(node: TreeNode<K>, depth: number): number {
    const children = node.children;
    let x: number;
    if (!children || children.length === 0) {
      x = SIDE + leafCount * COL;
      leafCount++;
      maxDepthEdges = Math.max(maxDepthEdges, depth);
    } else {
      const xs = children.map((child) => computeX(child.node, depth + 1));
      x = (xs[0]! + xs[xs.length - 1]!) / 2;
    }
    positions.set(node, { x, y: TOP + depth * VSTEP });
    return x;
  }
  computeX(root, 0);

  return { positions, leafCount, maxDepthEdges };
}

export function createTree<K extends string = string>(opts: CreateTreeOptions<K>): TreeHandle<K> {
  let root: TreeNode<K> = opts.initial;

  const svg = d3.create('svg').attr('class', 'tree-diagram').node()!;
  opts.container.appendChild(svg);

  function update(next: TreeNode<K>) {
    root = next;
    render();
  }

  /**
   * Recompute the leaf-column layout and size the SVG to its content — no
   * percentage scaling against a fixed viewBox, so the card's height tracks
   * the tree's actual depth/breadth. Re-run on every render: add/remove/edit
   * all change the tree's shape.
   */
  function render() {
    const { positions, leafCount, maxDepthEdges } = computeLayout(root);
    const viewW = 2 * SIDE + (leafCount - 1) * COL;
    const viewH = TOP + maxDepthEdges * VSTEP + BOTTOM;

    d3.select(svg)
      .attr('viewBox', `0 0 ${viewW} ${viewH}`)
      .attr('width', viewW)
      .attr('height', viewH)
      .selectAll('*')
      .remove();
    const g = d3.select(svg).append('g');
    draw(g, root, 0, positions);
  }

  function draw(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    node: TreeNode<K>,
    depth: number,
    positions: Map<TreeNode<K>, Pos>,
  ): void {
    const { x, y } = positions.get(node)!;
    const children = node.children;

    g.append('text')
      .attr('x', x)
      .attr('y', y)
      .attr('text-anchor', 'middle')
      .attr('class', 'tree-node')
      .text(opts.i18n.t(node.labelKey));

    if (children && children.length > 0) {
      children.forEach((child, i) => {
        const cpos = positions.get(child.node)!;
        g.append('line')
          .attr('x1', x)
          .attr('y1', y + 8)
          .attr('x2', cpos.x)
          .attr('y2', cpos.y - 12)
          .attr('class', 'tree-edge');

        const probText = g
          .append('text')
          .attr('x', (x + cpos.x) / 2)
          .attr('y', (y + cpos.y) / 2)
          .attr('text-anchor', 'middle')
          .attr('class', opts.editable ? 'tree-prob tree-prob-editable' : 'tree-prob')
          .text(child.probability.toFixed(2));

        if (opts.editable) {
          probText.attr('title', 'Edit probability').on('click', function () {
            openEditor(this, children, i);
          });
        }

        draw(g, child.node, depth + 1, positions);
      });
    }
  }

  /**
   * Apply `rebalance(node.children, index, newProbability)` to EVERY
   * internal node of the tree (walked from `root`) whose children array has
   * more than `index` entries — `editScope: 'tree'`'s "changed everywhere"
   * semantics for the repeated-event custom preset, where every occurrence
   * shares the same distribution.
   */
  function rebalanceEveryNode(node: TreeNode<K>, index: number, newProbability: number): void {
    if (node.children && node.children.length > index) {
      rebalance(node.children, index, newProbability);
    }
    for (const child of node.children ?? []) {
      rebalanceEveryNode(child.node, index, newProbability);
    }
  }

  /**
   * Open an absolutely-positioned `<input>` over `textEl` to edit
   * `children[index].probability`. Enter/blur commits via `rebalance`
   * (`editScope: 'node'`, the default) or `rebalanceEveryNode`
   * (`editScope: 'tree'`), then a re-render (so stale probability labels
   * update too), then `opts.onChange(root)` — the contract is always the
   * tree ROOT, never just the edited row's parent, so callers don't need to
   * know the edited node's depth. Escape cancels. Only one editor is open at
   * a time.
   */
  function openEditor(
    textEl: SVGTextElement,
    children: Array<{ probability: number; node: TreeNode<K> }>,
    index: number,
  ): void {
    opts.container.querySelector('.tree-prob-input')?.remove();

    const input = document.createElement('input');
    input.type = 'number';
    input.step = '0.05';
    input.min = '0';
    input.max = '1';
    input.className = 'tree-prob-input';
    input.value = children[index]!.probability.toFixed(2);
    positionEditor(input, textEl, opts.container);

    let settled = false;

    function commit(): void {
      if (settled) return;
      settled = true;
      const v = Number(input.value);
      if (Number.isFinite(v)) {
        if (opts.editScope === 'tree') {
          rebalanceEveryNode(root, index, v);
        } else {
          rebalance(children, index, v);
        }
        render();
        opts.onChange?.(root);
      }
      input.remove();
    }

    function cancel(): void {
      if (settled) return;
      settled = true;
      input.remove();
    }

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        commit();
      } else if (e.key === 'Escape') {
        cancel();
      }
    });
    input.addEventListener('blur', commit);

    opts.container.appendChild(input);
    input.focus();
    input.select();
  }

  render();

  return {
    element: svg,
    update,
    destroy() {
      svg.remove();
    },
  };
}

/**
 * Position `input` over `textEl`, relative to `container`. jsdom (unit
 * tests) has no layout engine, so `getBoundingClientRect()` always returns a
 * zeroed rect there — fall back to the `<text>` element's own `x`/`y`
 * attributes (which ARE meaningful, since `render` sets them) so the editor
 * still lands near the label instead of always at (0, 0). Adds the
 * container's scroll offset in both branches — wide trees scroll inside
 * `.pd-tree-body`, so a viewport-relative position alone would drift out
 * from under the label once the user has scrolled.
 */
function positionEditor(
  input: HTMLInputElement,
  textEl: SVGTextElement,
  container: HTMLElement,
): void {
  const textRect = textEl.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const hasLayout =
    textRect.width > 0 || textRect.height > 0 || textRect.top !== 0 || textRect.left !== 0;

  if (hasLayout) {
    input.style.left = `${textRect.left - containerRect.left + container.scrollLeft}px`;
    input.style.top = `${textRect.top - containerRect.top + container.scrollTop}px`;
  } else {
    const x = Number(textEl.getAttribute('x') ?? 0);
    const y = Number(textEl.getAttribute('y') ?? 0);
    input.style.left = `${x + container.scrollLeft}px`;
    input.style.top = `${y + container.scrollTop}px`;
  }
}
